import { useEffect, useMemo, useState } from "react";
import { fetchGame, fetchPerformance } from "../lib/api";
import type { ProductGame } from "../types/product";
import { TeamIdentity } from "../components/TeamIdentity";

type Result = "WIN" | "LOSS" | "PUSH";

interface MarketPlay {
  available: boolean;
  model_play: string;
  confidence: number | null;
  result: Result | null;
}

interface ReplayGame {
  game_id: string;
  season: number;
  week: number;
  game_date_utc: string;
  away_team: string;
  away_team_abbr: string;
  home_team: string;
  home_team_abbr: string;
  away_score: number;
  home_score: number;
  first_half_total: number;
  first_half_home_margin: number;
  plays: {
    spread: MarketPlay;
    total: MarketPlay;
  };
}

interface ResultCell {
  plays: number;
  wins: number;
  losses: number;
  pushes: number;
  record: string;
  win_rate: number | null;
}

interface SeasonResult {
  season: number | string;
  spread: ResultCell;
  total: ResultCell;
  combined: ResultCell;
}

interface ReplayPayload {
  replay_version: string;
  default_game_id: string;
  examples: ReplayGame[];
  season_results: SeasonResult[];
}

function formatDate(value: string): string {
  const day = value.slice(0, 10);
  const parsed = new Date(`${day}T12:00:00Z`);

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatPct(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function resultClass(result: Result | null): string {
  if (result === "WIN") return "r2-result r2-result-win";
  if (result === "LOSS") return "r2-result r2-result-loss";
  if (result === "PUSH") return "r2-result r2-result-push";
  return "r2-result r2-result-none";
}

function PredictionCard({
  title,
  play,
}: {
  title: string;
  play: MarketPlay;
}) {
  const confidence =
    play.confidence !== null && Number.isFinite(play.confidence)
      ? Math.max(0, Math.min(100, play.confidence * 100))
      : 0;

  return (
    <article className="r2-prediction-card">
      <div className="r2-prediction-top">
        <span>{title}</span>
        <strong className={resultClass(play.result)}>
          {play.result ?? "—"}
        </strong>
      </div>

      <div className="r2-model-play">
        <span>MODEL PLAY</span>
        <strong>{play.available ? play.model_play : "—"}</strong>
      </div>

      <div className="r2-confidence-row">
        <div>
          <span>CONFIDENCE</span>
          <strong>{formatPct(play.confidence)}</strong>
        </div>

        <div className="r2-confidence-track" aria-hidden="true">
          <span style={{ width: `${confidence}%` }} />
        </div>
      </div>
    </article>
  );
}

function SeasonCard({ row }: { row: SeasonResult }) {
  return (
    <article className="r2-season-card">
      <div className="r2-season-top">
        <span>{row.season}</span>
        <div>
          <strong>{row.combined.record}</strong>
          <small>{formatPct(row.combined.win_rate)}</small>
        </div>
      </div>

      <div className="r2-season-lines">
        <div>
          <span>ATS</span>
          <strong>{row.spread.record}</strong>
          <small>{formatPct(row.spread.win_rate)}</small>
        </div>
        <div>
          <span>TOTAL</span>
          <strong>{row.total.record}</strong>
          <small>{formatPct(row.total.win_rate)}</small>
        </div>
      </div>
    </article>
  );
}

export function PerformancePage() {
  const [data, setData] = useState<ReplayPayload | null>(null);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [loadedGame, setLoadedGame] = useState<ProductGame | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPerformance()
      .then((payload) => {
        const replay = payload as unknown as ReplayPayload;
        setData(replay);
        setSelectedGameId(
          replay.default_game_id || replay.examples[0]?.game_id || "",
        );
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : "Unable to load the replay.",
        );
      });
  }, []);

  const selected = useMemo(
    () =>
      data?.examples.find((game) => game.game_id === selectedGameId) ?? null,
    [data, selectedGameId],
  );

  const seasonRows = useMemo(
    () =>
      data?.season_results.filter(
        (row) =>
          typeof row.season === "number" &&
          row.season >= 2023 &&
          row.season <= 2025,
      ) ?? [],
    [data],
  );

  const threeYear = useMemo(
    () =>
      data?.season_results.find(
        (row) => String(row.season).toUpperCase() === "3-YEAR",
      ) ?? null,
    [data],
  );

  async function runReplay() {
    if (!selectedGameId) return;

    setRunning(true);
    setError(null);

    try {
      const game = await fetchGame(selectedGameId);
      setLoadedGame(game);
    } catch (err: unknown) {
      setLoadedGame(null);
      setError(
        err instanceof Error ? err.message : "Unable to pull halftime data.",
      );
    } finally {
      setRunning(false);
    }
  }

  if (error) {
    return <div className="empty-state error">{error}</div>;
  }

  if (!data || !selected) {
    return <div className="empty-state">Loading 2025 replay…</div>;
  }

  const awayScore = loadedGame?.halftime.away_score ?? 0;
  const homeScore = loadedGame?.halftime.home_score ?? 0;

  return (
    <div className="page-stack r2-replay-page">
      <section className="r2-hero">
        <div className="r2-hero-copy">
          <div className="eyebrow">2025 HISTORICAL REPLAY</div>
          <h1>Halftime Model Replay</h1>
          <p>Pull the first-half game state and show the model call.</p>
        </div>

        <div className="r2-controls">
          <label htmlFor="r2-replay-game">Game</label>
          <select
            id="r2-replay-game"
            value={selectedGameId}
            onChange={(event) => {
              setSelectedGameId(event.target.value);
              setLoadedGame(null);
            }}
          >
            {data.examples.map((game) => (
              <option value={game.game_id} key={game.game_id}>
                Week {game.week} · {game.away_team_abbr} @{" "}
                {game.home_team_abbr}
              </option>
            ))}
          </select>

          <button type="button" onClick={runReplay} disabled={running}>
            {running ? "RUNNING…" : "PULL 1H DATA + RUN MODEL"}
          </button>
        </div>
      </section>

      {loadedGame ? (
        <>
          <section className="r2-live-strip">
            <span />
            HALFTIME DATA LOADED
          </section>

          <section className="r2-scoreboard">
            <TeamIdentity
              abbr={loadedGame.game.away_team_abbr}
              name={loadedGame.game.away_team}
              score={awayScore}
              side="away"
            />

            <div className="r2-score-center">
              <span>HALFTIME</span>
              <strong>
                {awayScore} - {homeScore}
              </strong>
              <small>
                Week {loadedGame.game.week} ·{" "}
                {formatDate(loadedGame.game.game_date_utc)}
              </small>
            </div>

            <TeamIdentity
              abbr={loadedGame.game.home_team_abbr}
              name={loadedGame.game.home_team}
              score={homeScore}
              side="home"
            />
          </section>

          <section className="r2-prediction-grid">
            <PredictionCard
              title="2H SPREAD"
              play={selected.plays.spread}
            />
            <PredictionCard
              title="2H TOTAL"
              play={selected.plays.total}
            />
          </section>
        </>
      ) : (
        <section className="r2-ready-state">
          <span>READY</span>
          <strong>Select a 2025 game and run the model.</strong>
        </section>
      )}

      <section className="r2-history">
        <div className="r2-history-head">
          <div>
            <div className="eyebrow">2023–2025 RESULTS</div>
            <h2>Historical Model Plays</h2>
          </div>

          {threeYear ? (
            <div className="r2-three-year">
              <span>3-YEAR</span>
              <strong>{threeYear.combined.record}</strong>
              <small>{formatPct(threeYear.combined.win_rate)} win</small>
            </div>
          ) : null}
        </div>

        <div className="r2-season-grid">
          {seasonRows.map((row) => (
            <SeasonCard row={row} key={String(row.season)} />
          ))}
        </div>
      </section>
    </div>
  );
}
